import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal, computed, input } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyService } from '../../api/api/company.service';
import { LayoutService } from '../../../services/LayoutService';
import { AuthService } from '../../../services/AuthService';
import { firstValueFrom } from 'rxjs';
import dayjs from 'dayjs/esm';
import './employees-card.js';
import './availability-card.js';
import './shifts-scheduled-card.js';
import './trial-card.js';

@Component({
  selector: 'app-info-cards',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    @if (hasValidData()) {
      <div
        class="cards-grid p-4"
        [style.--cols]="cardCount()">
        <employees-card
          [attr.theme]="layout.theme()"
          [attr.lang]="auth.current_locale()"
          [data]="snapshotData()"
          (cardclick)="onCardClick($event)">
        </employees-card>
        @if (showShiftManager()) {
          <availability-card
            week="current"
            color="blue"
            [attr.theme]="layout.theme()"
            [attr.lang]="auth.current_locale()"
            [data]="snapshotData()"
            (cardclick)="onCardClick($event)">
          </availability-card>
          <availability-card
            week="next"
            color="indigo"
            [attr.theme]="layout.theme()"
            [attr.lang]="auth.current_locale()"
            [data]="snapshotData()"
            (cardclick)="onCardClick($event)">
          </availability-card>
          <shifts-scheduled-card
            [attr.theme]="layout.theme()"
            [attr.lang]="auth.current_locale()"
            [data]="snapshotData()"
            (cardclick)="onCardClick($event)">
          </shifts-scheduled-card>
        }
        @if (isTrial()) {
          <trial-card
            [attr.days-left]="trialDaysLeft()"
            [attr.theme]="layout.theme()"
            [attr.lang]="auth.current_locale()"
            (cardclick)="onCardClick($event)">
          </trial-card>
        }
      </div>
    }
  `,
  styles: [`
    .cards-grid {
      display: grid;
      gap: 1.5rem;
      align-items: stretch;
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    @media (min-width: 640px) {
      .cards-grid {
        grid-template-columns: repeat(min(2, var(--cols, 1)), minmax(0, 1fr));
      }
    }

    @media (min-width: 1024px) {
      .cards-grid {
        grid-template-columns: repeat(min(4, var(--cols, 1)), minmax(0, 1fr));
      }
    }

    @media (min-width: 1280px) {
      .cards-grid {
        grid-template-columns: repeat(var(--cols, 1), minmax(0, 1fr));
      }
    }

    employees-card,
    availability-card,
    shifts-scheduled-card,
    trial-card {
      display: block;
      height: 100%;
    }
  `]
})
export class InfoCardsWrapperComponent implements OnInit {
  public layout = inject(LayoutService);
  public auth = inject(AuthService);
  private companyApi = inject(CompanyService);
  private router = inject(Router);

  public showShiftManager = input<boolean>(true);
  public showTaskManager = input<boolean>(true);

  public snapshotData = signal<any>(null);

  public isTrial = computed(() => {
    const company = this.auth.currentUser()?.company;
    const status = company?.status || (company as any)?.status;
    return status === 'trial';
  });

  public trialDaysLeft = computed(() => {
    const company = this.auth.currentUser()?.company;
    if (!company?.date_created) return 14;
    const created = dayjs(company.date_created);
    const trialEnd = created.add(14, 'day');
    const days = trialEnd.diff(dayjs(), 'day');
    return Math.max(0, days);
  });

  public cardCount = computed(() => {
    let count = 1;
    if (this.showShiftManager()) {
      count += 3;
    }
    if (this.isTrial()) {
      count += 1;
    }
    return count;
  });

  public hasValidData = computed(() => {
    const data = this.snapshotData();
    return data && Array.isArray(data.services) && data.services.length > 0;
  });

  ngOnInit() {
    this.fetchSnapshot();
  }

  onCardClick(event: Event) {
    const customEvent = event as CustomEvent<{ route: string }>;
    if (customEvent.detail?.route) {
      this.router.navigateByUrl(customEvent.detail.route);
    }
  }

  private async fetchSnapshot() {
    try {
      const res = await firstValueFrom(this.companyApi.apiCompanyServicesSnapshotGet());
      if (res && Array.isArray((res as any).services)) {
        this.snapshotData.set(res);
      } else {
        this.snapshotData.set(null);
      }
    } catch (err) {
      console.error('Failed to load services snapshot:', err);
      this.snapshotData.set(null);
    }
  }
}
